import zIndex from "@mui/material/styles/zIndex";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { ShieldCheck, Cpu, BarChart3, ArrowRight, CheckCircle2, AlertTriangle, Target, DollarSign, Globe, Users, Building2, Layers, Lock, TrendingUp, Zap, FileCheck } from "lucide-react";

interface LLMProblemSolutionModalProps {
  open: boolean;
  onClose: () => void;
}

export const LLMProblemSolutionModal = ({ open, onClose }: LLMProblemSolutionModalProps) => (
  <Dialog       open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="z-[99999] w-[80vw] max-w-[80vw] h-[80vh] max-h-[80vh] p-0 gap-0 overflow-hidden">
      <DialogHeader className="p-6 pb-4 border-b border-border/50 bg-secondary/30" style={{ borderBottomColor: "#E3E8EF" }}>
        <DialogTitle className="font-display text-2xl font-bold text-foreground flex items-center gap-3" style={{ color: "#111827" }}>
          <div className="w-10 h-10 rounded-xl bg-ticca-blue/10 flex items-center justify-center">
            <Cpu className="w-5 h-5 text-ticca-blue" />
          </div>
          LLM Problem & Solution
        </DialogTitle>
        <p className="text-sm text-muted-foreground mt-1" style={{ color: "#64748b" }}>
          TICCA AI Economic Control Plane — Strategic Business Case
        </p>
      </DialogHeader>

      <ScrollArea className="h-[calc(80vh-80px)]">
        <div className="p-6 space-y-8" style={{ color: "#111827" }}>

          {/* Executive Summary */}
          <Section title="Executive Summary" icon={<Target className="w-5 h-5 text-ticca-blue" />}>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              The most commercially viable opportunity for TICCA is <strong className="text-foreground">not</strong> to become a frontier-model company. The real opportunity is to build and sell the <strong className="text-foreground">economic control layer above LLMs</strong>.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {[
                "Reduce LLM and AI-agent operating costs",
                "Route work to the cheapest acceptable model",
                "Track AI work, human review, and output ownership",
                "Enforce governance, sovereignty, and auditability",
                "Connect AI usage to budgets, projects, teams",
                "Measurable business outcomes",
              ].map((item) => (
                <div key={item} className="flex items-start gap-2 text-sm text-foreground/80 p-2.5 rounded-lg bg-ticca-blue/[0.03] border border-ticca-blue/10" style={{ color: "#111827", borderColor: "#E3E8EF" }}>
                  <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-ticca-blue" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <Highlight color="blue">
              <strong>TICCA AI Economic Control Plane</strong> — A B2B SaaS platform that sits above OpenAI, Anthropic, Google, open-source models, private inference clusters, and future sovereign deployments.
            </Highlight>
          </Section>

          {/* Business Model */}
          <Section title="Revenue Architecture" icon={<DollarSign className="w-5 h-5 text-ticca-green" />}>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {[
                { label: "MRU", desc: "User subscriptions for teams & workspaces", range: "" },
                { label: "PPU", desc: "Metered AI usage with orchestration fee", range: "" },
                { label: "Agent Subs", desc: "Per active AI agent/worker", range: "" },
                { label: "Governance", desc: "Compliance, sovereignty, audit add-ons", range: "" },
              ].map((item) => (
                <div key={item.label} className="p-4 rounded-xl bg-secondary/40 border border-border/40" style={{ borderColor: "#E3E8EF", color: "#111827" }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-bold text-foreground" style={{ color: "#111827" }}>{item.label}</span>
                    <span className="text-xs font-semibold text-ticca-green">{item.range}</span>
                  </div>
                  <p className="text-xs text-muted-foreground" style={{ color: "#64748b" }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* Core Problem */}
          <Section title="The Core Problem" icon={<AlertTriangle className="w-5 h-5 text-ticca-red" />}>
            <div className="space-y-3 mb-4">
              {[
                { title: "AI infrastructure economics are brutal", desc: "Very high compute cost, ongoing retraining, rapid hardware obsolescence, vendor concentration risk." },
                { title: "Customers becoming multi-model & price sensitive", desc: "Enterprises want optionality, lower cost, data residency, auditability, and cost attribution." },
                { title: "Market overserves intelligence, underserves economics", desc: "The missing layer is not another chatbot — it's a business operating system for AI work." },
              ].map((item) => (
                <div key={item.title} className="p-4 rounded-xl bg-ticca-red/[0.03] border border-ticca-red/10" style={{ borderColor: "#E3E8EF", color: "#111827" }}>
                  <h4 className="text-sm font-bold text-foreground mb-1" style={{ color: "#111827" }}>{item.title}</h4>
                  <p className="text-xs text-muted-foreground" style={{ color: "#64748b" }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* Solution */}
          <Section title="Solution" icon={<ShieldCheck className="w-5 h-5 text-ticca-blue" />}>
            <p className="text-sm text-muted-foreground mb-4">The platform functions as a model router, cost governor, workflow orchestrator, work ledger, policy enforcement layer, and enterprise analytics surface.</p>
            <div className="grid sm:grid-cols-3 gap-3 mb-4">
              {[
                { title: "Task-to-Model Routing", desc: "Classify work, send to cheapest acceptable engine" },
                { title: "Cost Attribution", desc: "Every AI interaction linked to user, team, project, cost centre" },
                { title: "Human + AI Ledger", desc: "Verifiable chain of contribution and accountability" },
                { title: "Budget Controls", desc: "Spend caps, approved models, geographic restrictions" },
                { title: "BYO-Model Support", desc: "Connect OpenAI, Anthropic, Google, private endpoints" },
                { title: "Agent Management", desc: "AI agents as managed workers with roles & budgets" },
              ].map((item) => (
                <div key={item.title} className="p-3 rounded-lg bg-ticca-blue/[0.04] border border-ticca-blue/15">
                  <h4 className="text-xs font-bold text-foreground mb-1">{item.title}</h4>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* Product Modules */}
          <Section title="Product Modules" icon={<Layers className="w-5 h-5 text-ticca-orange" />}>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { name: "TICCA Route", desc: "Model and provider routing engine" },
                { name: "TICCA Ledger", desc: "Immutable work and cost ledger for AI + human collaboration" },
                { name: "TICCA Guard", desc: "Governance, policy, redaction, approvals, compliance controls" },
                { name: "TICCA Treasury-AI", desc: "Financial analytics for token usage, budgets, margin tracking" },
                { name: "TICCA AgentOps", desc: "Runtime management for AI agents, budgets, and oversight" },
                { name: "TICCA Sovereign", desc: "Regional data residency and private hosting deployment" },
              ].map((m) => (
                <div key={m.name} className="flex items-start gap-3 p-3 rounded-lg bg-ticca-orange/[0.03] border border-ticca-orange/10">
                  <ArrowRight className="w-3.5 h-3.5 mt-0.5 shrink-0 text-ticca-orange" />
                  <div>
                    <span className="text-sm font-bold text-foreground">{m.name}</span>
                    <p className="text-xs text-muted-foreground">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Unit Economics */}
          <Section title="Illustrative Unit Economics" icon={<BarChart3 className="w-5 h-5 text-ticca-green" />}>
            <div className="grid sm:grid-cols-3 gap-3 mb-4">
              {[
                { segment: "SME", users: "100 users, 10 agents", revenue: "$4,346/mo", margin: "~78%", savings: "17.8%" },
                { segment: "Mid-Market", users: "500 users, 60 agents", revenue: "$23,140/mo", margin: "~80%", savings: "19.8%" },
                { segment: "Enterprise", users: "2,000 users, 300 agents", revenue: "$100,900/mo", margin: "~82%", savings: "16.9%" },
              ].map((s) => (
                <div key={s.segment} className="p-4 rounded-xl bg-secondary/40 border border-border/40">
                  <h4 className="text-sm font-bold text-foreground mb-2">{s.segment}</h4>
                  <p className="text-xs text-muted-foreground mb-1">{s.users}</p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Revenue</span>
                      <span className="font-bold text-ticca-green">{s.revenue}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Gross Margin</span>
                      <span className="font-bold text-foreground">{s.margin}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Customer Savings</span>
                      <span className="font-bold text-ticca-blue">{s.savings}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Highlight color="green">
              Break-even at ~45 SME customers, ~9 mid-market, or ~2 enterprise customers with $150K/mo fixed operating costs.
            </Highlight>
          </Section>

          {/* Competitive Advantage */}
          <Section title="Competitive Advantage" icon={<Lock className="w-5 h-5 text-ticca-blue" />}>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <div className="p-4 rounded-xl bg-ticca-blue/[0.03] border border-ticca-blue/10">
                <h4 className="text-sm font-bold text-foreground mb-2">Sells Control, Not Compute</h4>
                <p className="text-xs text-muted-foreground">TICCA creates stronger retention than model-only vendors through workflow logic, budget controls, governance rules, and audit history.</p>
              </div>
              <div className="p-4 rounded-xl bg-ticca-green/[0.03] border border-ticca-green/10">
                <h4 className="text-sm font-bold text-foreground mb-2">Remains Asset-Light</h4>
                <p className="text-xs text-muted-foreground">Avoids massive GPU capex, chip depreciation risk, direct data-center financing, and wholesale power/cooling risk.</p>
              </div>
            </div>
          </Section>

          {/* Best Initial Markets */}
          <Section title="Best Initial Market Segments" icon={<Users className="w-5 h-5 text-ticca-orange" />}>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {[
                { name: "Professional Services", examples: "Consulting, legal, accounting, research" },
                { name: "Software & Product", examples: "Engineering, support, QA, product ops" },
                { name: "BPO & Remote Operations", examples: "Outsourced teams, service desks, content" },
                { name: "Regulated Mid-Market", examples: "Finance, health, insurance, gov suppliers" },
              ].map((seg) => (
                <div key={seg.name} className="p-3 rounded-lg bg-secondary/30 border border-border/30">
                  <h4 className="text-xs font-bold text-foreground mb-1">{seg.name}</h4>
                  <p className="text-xs text-muted-foreground">{seg.examples}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* GTM */}
          <Section title="Go-To-Market Strategy" icon={<Globe className="w-5 h-5 text-ticca-blue" />}>
            <Highlight color="blue">
              <strong>Core Promise:</strong> Reduce AI operating costs, govern AI agents, and prove business value across every project and team.
            </Highlight>
            <div className="mt-4 space-y-2">
              <h4 className="text-sm font-bold text-foreground">Land-and-Expand Model</h4>
              <div className="flex flex-wrap gap-2">
                {["Audit spend", "Pilot one team", "Demonstrate savings", "Expand teams", "Add AgentOps", "Full TICCA stack"].map((step, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-full bg-ticca-blue/8 border border-ticca-blue/15 text-xs font-medium text-foreground">
                    <span className="text-ticca-blue font-bold mr-1">{i + 1}.</span>{step}
                  </span>
                ))}
              </div>
            </div>
          </Section>

          {/* Roadmap
          <Section title="Product Roadmap" icon={<TrendingUp className="w-5 h-5 text-ticca-green" />}>
            <div className="space-y-3">
              {[
                { phase: "Phase 1 — 90 days", items: "Model routing, provider abstraction, usage metering, attribution dashboards, approval rules" },
                { phase: "Phase 2 — 180 days", items: "Agent budgets, work ledgers, human review, sovereign deployment, API gateway" },
                { phase: "Phase 3 — 12 months", items: "Private model connectors, advanced audit, optimization recommendations, autonomous agent queues" },
                { phase: "Phase 4 — Selective Internal", items: "Smaller local models for internal tasks, hybrid routing, customer-specific inference tiers" },
              ].map((p) => (
                <div key={p.phase} className="p-3 rounded-lg bg-secondary/30 border border-border/30">
                  <h4 className="text-xs font-bold text-foreground mb-1">{p.phase}</h4>
                  <p className="text-xs text-muted-foreground">{p.items}</p>
                </div>
              ))}
            </div>
          </Section> */}

          {/* Strategic Conclusion */}
          <Section title="Strategic Positioning" icon={<Zap className="w-5 h-5 text-ticca-orange" />}>
            <div className="p-5 rounded-xl bg-gradient-to-br from-ticca-blue/[0.05] to-ticca-green/[0.05] border border-ticca-blue/15">
              <p className="text-base font-display font-bold text-foreground mb-3">
                TICCA is the operating and economic control layer for enterprise AI work.
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                It helps businesses route work to the right model, control spend, govern AI agents, verify contribution, and connect AI activity to projects, budgets, and measurable outcomes.
              </p>
              <div className="space-y-2">
                {[
                  "Build the AI Spend & Governance Audit offer",
                  "Build the routing + usage + ledger MVP",
                  "Target 3 pilot customers",
                  "Prove 15%+ customer savings",
                  "Convert into annual SaaS contracts",
                ].map((action, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-foreground">
                    <span className="w-5 h-5 rounded-full bg-ticca-blue/10 flex items-center justify-center text-xs font-bold text-ticca-blue">{i + 1}</span>
                    {action}
                  </div>
                ))}
              </div>
            </div>
          </Section>

          {/* Risks */}
          <Section title="Risk Review" icon={<FileCheck className="w-5 h-5 text-ticca-red" />}>
            <div className="space-y-2">
              {[
                { risk: "Model vendors add these features", mitigation: "Stay vendor-neutral and workflow-centric" },
                { risk: "Savings claims overstated", mitigation: "Sell measured savings from actual workload profiling" },
                { risk: "Customers resist another platform", mitigation: "Position as unification layer, not replacement" },
                { risk: "Open-source reduces external spend", mitigation: "Route to self-hosted models; charge for governance" },
                { risk: "Enterprise sales cycles slow", mitigation: "Use paid audits and departmental pilots" },
              ].map((r) => (
                <div key={r.risk} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/20 border border-border/30">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-ticca-red" />
                  <div>
                    <span className="text-xs font-bold text-foreground">{r.risk}</span>
                    <span className="text-xs text-muted-foreground"> — {r.mitigation}</span>
                  </div>
                </div>
              ))}
            </div>
          </Section>

        </div>
      </ScrollArea>
    </DialogContent>
  </Dialog>
);

// Helper components
const Section = ({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h3 className="font-display font-bold text-lg text-foreground">{title}</h3>
    </div>
    {children}
  </div>
);

const Highlight = ({ color, children }: { color: "blue" | "green" | "orange" | "red"; children: React.ReactNode }) => {
  const colors = {
    blue: "bg-ticca-blue/[0.03] border-ticca-blue/10",
    green: "bg-ticca-blue/[0.03] border-ticca-blue/10", // force solution highlight to blue
    orange: "bg-ticca-orange/[0.03] border-ticca-orange/10",
    red: "bg-ticca-red/[0.03] border-ticca-red/10",
  };
  return (
    <div className={`p-3.5 rounded-xl border ${colors[color]}`}>
      <p className="text-sm text-foreground">{children}</p>
    </div>
  );
};
